import * as React from 'react'
import {
    Dispatch,
    FormEvent,
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    RefObject,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import * as Yup from 'yup'
import { Schema } from 'yup'
import { Formik, useFormikContext } from 'formik'
import { IconButton, ITextField, ITextFieldProps, mergeStyleSets, Stack, StackItem } from '@fluentui/react'
import { InvoiceStatus } from '../../../config/finance/invoiceConfig'
import { Billable, mapTimeOverviewDtoToMutation } from '../../../config/registration/timeConfig'
import { BudgetMonitoringDto, IFileDto, RateDto } from '../../../config/fileConfig'
import { useSelector } from 'react-redux'
import { Store } from '../../../reducers'
import DurationTextField from '../DurationTextField'
import MoneyField from '../MoneyField'
import {
    convertNumber,
    formatInlineCurrency,
    formatTimeInMinutes,
    multiplyTime,
    multiplyTimeNumber,
    parseNumber,
    parseTime,
    parseTimeInHours,
    parseTimeInMinutes,
} from '../../../helpers/numberHelper'
import Form, { EntityFormProps } from '../Form'
import { formatIsoDate, parseDate } from '../../../helpers/dateHelper'
import { TimeMutationDto, TimeOverviewDto } from '../../../config/registrationConfig'
import FormikTextField from '../../../components/Formik/TextField/FormikTextField'
import MessageContext from '../../../context/MessageContext'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'
import FormContainer from '../../../components/Form/FormContainer'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { NormalizedError } from '../../../reducers/errorReducer'
import { useSettings } from '../../../hooks/useSettings'
import { FileRateType, ModuleType, TimeCategory } from '../../../context/GlobalContext'
import useRoles, { rolesWriteAllTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { BudgetMonitoringModuleDto, useFileModule } from '../../../hooks/module/useFileModule'
import { Field, MessageBar, MessageBarBody, Radio, RadioGroup, RadioProps } from '@fluentui/react-components'
import { useTimeDivisorSetting } from '../../../hooks/file/useTimeDivisorSetting'
import { useTimeCategoriesSetting } from '../../../hooks/file/useTimeCategoriesSetting'
import { IRefObject } from '@fluentui/utilities'
import { useTranslation } from 'react-i18next'
import { BillableLabels } from '../../TagPicker/BillablePicker'
import useCurrency from '../../../hooks/useCurrency'
import { BaseTierDto, isActiveTier, isBaseUserTier } from '../../../config/finance/financeConfig'
import OverflowButtonPanel from '../../Overflow/OverflowButtonPanel'
import FileCombobox from '../../Combobox/FileCombobox'
import UserCombobox from '../../Combobox/UserCombobox'
import LocalDatePicker from '../../DatePicker/LocalDatePicker'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../Combobox/EnumMapCombobox'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import { usePrevious } from '@fluentui/react-hooks'

type Props = PropsWithChildren<EntityFormProps<TimeOverviewDto, TimeMutationDto>> & { globalError?: NormalizedError }

const getValidationSchema = (hasTimeCategoryModule: boolean, translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        category: hasTimeCategoryModule
            ? Yup.string().label(translate('time.category.label')).required()
            : Yup.string().label(translate('time.category.label')).nullable(),
        description: Yup.string().label(translate('time.description')).required(),
        fileId: Yup.string().uuid().label(translate('time.fileid')).required(),
        duration: Yup.string()
            .required()
            .test('is-valid', translate('time.duration.isvalid'), value => !isNaN(parseTimeInMinutes(value)))
            .test('is-valid-minutes', translate('time.duration.isvalidminutes'), value => parseTime(value).minutes < 60)
            .test('is-less-than-a-minute', translate('time.duration.islessminute'), value => parseTimeInMinutes(value) >= 1)
            .test('is-more-than-a-day', translate('time.duration.ismoreday'), value => parseTimeInMinutes(value) <= 24 * 60),
        rate: Yup.string().label(translate('time.rate')).required(),
        userId: Yup.string().uuid().label(translate('time.userid')).required(),
        date: Yup.string().label(translate('time.date')).required(),
        billable: Yup.string().label(translate('time.billable')).required(),
    })
}

const billableOptions: Omit<RadioProps, 'label'>[] = [{ value: Billable.Billable }, { value: Billable.Offered }, { value: Billable.NotBillable }]

const getRateValue = (initialValues: TimeMutationDto, values: TimeMutationDto, rates: RateDto<BaseTierDto>): string => {
    if (
        values.userId === initialValues.userId &&
        values.fileId === initialValues.fileId &&
        values.billable === initialValues.billable &&
        values.billable === Billable.Billable &&
        values.date === initialValues.date &&
        initialValues.rate
    ) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.rate
    }
    if (values.billable !== Billable.Billable) {
        return '0.00'
    }

    switch (rates.fileRateType) {
        case FileRateType.File:
            return rates.rate
        case FileRateType.UserTier:
            return (
                (rates.tiers || [])
                    .filter(tier => isActiveTier(tier, parseDate(values.date)))
                    .filter(tier => isBaseUserTier(tier))
                    .find(tier => tier.userId === values.userId)?.salesRate || '0.00'
            )
        case FileRateType.PartyTier:
            return (rates.tiers || []).find(tier => isActiveTier(tier, parseDate(values.date)))?.salesRate || '0.00'
    }
}

const getBillableValue = (
    initialValues: TimeMutationDto,
    values: TimeMutationDto,
    rates: RateDto<BaseTierDto>,
    timeCategories: TimeCategory[],
): Billable => {
    if (values.fileId === initialValues.fileId && values.category === initialValues.category && initialValues.billable) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.billable
    }

    const category = timeCategories?.find(category => category.key === values.category)
    return category?.billable ?? rates?.billable ?? values.billable ?? initialValues.billable
}

export enum Direction {
    // Transform from the field value to the DTO value.
    FieldToDto,
    // Transform from the DTO value to the field value.
    DtoToField,
}

export function transformCurrency(value: string, direction: Direction): string {
    switch (direction) {
        case Direction.DtoToField:
            return convertNumber(value, 'en', 'nl-nl', '0.00')
        case Direction.FieldToDto:
            return convertNumber(value, 'nl-nl', 'en', '0.00')
        default:
            return value
    }
}

export const TimeFormContainer: FunctionComponent<{
    entity: TimeOverviewDto
    useFileContext: boolean
    invoiceStatus?: InvoiceStatus
    currentFile?: IFileDto
    setValidationSchema: Dispatch<SetStateAction<Schema>>
}> = props => {
    const axios = useAxios()
    const settings = useSettings()
    const modules = useModules()
    const {
        hasTimeCategoriesModule: fileHasTimeCategoriesModule,
        timeCategories,
        defaultCategory: defaultTimeCategory,
        setCurrentFile: setTimeCategoriesFile,
    } = useTimeCategoriesSetting()
    const timeDivisorSetting = useTimeDivisorSetting()
    const { t: translate } = useTranslation()

    const timeCategoryOptions = useMemo<Map<string, string>>(
        () => new Map(timeCategories?.map(x => [x.key, x.name] as [string, string])),
        [timeCategories],
    )

    const hasTimeCategoriesModule = useMemo<boolean>(
        () =>
            (fileHasTimeCategoriesModule || settings?.modules?.find(mod => mod.type === ModuleType.TimeCategories)?.defaultActiveForFile) &&
            settings?.modules?.some(mod => mod.type === ModuleType.TimeCategories),
        [fileHasTimeCategoriesModule, settings],
    )

    const context = useFormikContext<TimeMutationDto>()
    const { initialValues, errors, values, handleChange, setFieldValue } = useFormikContext<TimeMutationDto>()
    const previousValues = usePrevious<TimeMutationDto>(values)

    const { fileIdMap } = useContext(MessageContext)

    const isInvoiceDraft: boolean = props.invoiceStatus === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean = props.invoiceStatus !== undefined && props.invoiceStatus !== InvoiceStatus.Draft

    const {
        fileModule: budgetMonitoringModule,
        has: hasBudgetMonitoringModule,
        setCurrentFile: setBudgetMonitoringModuleFile,
    } = useFileModule<BudgetMonitoringModuleDto>(ModuleType.BudgetMonitoring, props.useFileContext && props.currentFile)

    const visibleBillableOptions = useMemo<RadioProps[]>(
        () =>
            billableOptions.map(option => ({
                ...option,
                label: translate(BillableLabels.get(option.value as Billable).name),
                disabled: option.disabled,
            })),
        [values.billable],
    )

    //set fixed rate, but replace value once settings have been loaded
    const [rates, setRates] = useState<RateDto<BaseTierDto>>({
        fileRateType: FileRateType.File,
        rate: '0.00',
        currency: 'EUR',
    })
    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])
    const [budgetMonitoring, setBudgetMonitoring] = useState<BudgetMonitoringDto>(undefined)

    // These fields have a different display value than what they actually carry.
    const [rateField, setRateField] = useState<string>()
    const [turnoverField, setTurnoverField] = useState<string>()
    const [billableField, setBillableField] = useState<string>(Billable.Billable)

    //Check if the category field should use the initial value or live value
    const useInitialCategory = useMemo<boolean>(
        () => !!initialValues.category && initialValues.fileId === values.fileId,
        [initialValues, values.fileId],
    )
    const useInitialDescription = useMemo<boolean>(
        () => !!initialValues.description && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )
    //Check if the billable field should use the initial value or calculated value
    const useInitialBillable = useMemo<boolean>(
        () => !!initialValues.billable && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )

    const { isViewport, getGap } = useViewport()
    const roles = useRoles()

    const styles = mergeStyleSets({
        resetDescription: { backgroundColor: 'transparent' },
    })

    const fileFocusRef: IRefObject<HTMLInputElement> = React.createRef<HTMLInputElement>()
    const categoryFocusRef: IRefObject<HTMLInputElement> = React.createRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = React.createRef<ITextField>()
    const durationFocusRef: RefObject<ITextField> = React.createRef<ITextField>()

    useEffect(() => {
        setRateField(transformCurrency(initialValues.rate, Direction.DtoToField) as string)
        setBillableField(initialValues.billable)
    }, [initialValues])

    useEffect(() => {
        if (previousValues?.fileId !== values.fileId) {
            setFieldValue('category', undefined)
            return
        }
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!props.useFileContext && !values.fileId) {
            fileFocusRef.current.focus()
        } else if (!!categoryFocusRef.current && !values.category) {
            categoryFocusRef.current.focus()
        } else if (!!initialValues.description) {
            durationFocusRef.current.focus()
        } else if (!initialValues.description || !values.description) {
            descriptionFocusRef.current.focus()
        }
    }, [values.fileId, values.category])

    useEffect(() => {
        setRateField(transformCurrency(values.rate, Direction.DtoToField))
        setTurnoverField(multiplyTimeNumber(values.duration, values.rate, '0.00', 'en'))
    }, [values.rate, values.duration])

    useEffect(() => {
        // Always set the rate globally first.
        setRates({
            fileRateType: settings?.fileScaffold?.fileRate?.type || FileRateType.File,
            rate: settings?.fileScaffold?.fileRate?.hourRate || '0.00',
            currency: settings?.fileScaffold?.currency || 'EUR',
        })
        if (values.fileId) {
            // Fetch the rate when the file has changed.
            axios.get<RateDto<BaseTierDto>>(`/file/${values.fileId}/rate`).then(response => {
                setRates(response.data)
            })
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response => {
                setBudgetMonitoringModuleFile(response.data)
                setTimeCategoriesFile(response.data)
            })
        }
    }, [values.fileId])

    useEffect(() => {
        if (rates) {
            setFieldValue('rate', getRateValue(initialValues, values, rates))
            setFieldValue('currency', rates.currency)
        }
    }, [rates, values.userId, values.billable, values.date, values.duration])

    useEffect(() => {
        if (rates) {
            const billable = useInitialBillable ? initialValues.billable : getBillableValue(initialValues, values, rates, timeCategories)
            setBillableField(billable)
            setFieldValue('billable', billable)
        }
    }, [rates, values.fileId, values.category])

    useEffect(() => {
        if (hasBudgetMonitoringModule) {
            // Fetch the outstanding times when the file has changed.
            axios.get<BudgetMonitoringDto>(`/file/${values.fileId}/budget/monitoring`).then(response => {
                setBudgetMonitoring(response.data)
            })
        }
    }, [hasBudgetMonitoringModule])

    const exceedsBudget = useMemo<boolean>(() => {
        let currentAmount = 0
        if (budgetMonitoringModule && budgetMonitoringModule.hourThreshold > 0 && budgetMonitoring && values.duration) {
            currentAmount = budgetMonitoring.outstandingHours
            if (budgetMonitoringModule.includeBilled) {
                currentAmount += budgetMonitoring.billedHours
            }

            console.log(`TimeForm.budgetMonitoringModule: currentHours=${currentAmount}`)
            currentAmount += Math.max(0, parseTimeInMinutes(values.duration) / 60 - parseTimeInMinutes(props.entity?.duration || '0:00') / 60)
            console.log(`TimeForm.budgetMonitoringModule: newHours=${currentAmount}`)
            if (currentAmount > budgetMonitoringModule.hourThreshold) {
                return true
            }
        }

        currentAmount = 0
        if (budgetMonitoringModule && parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0 && budgetMonitoring && values.duration) {
            if (budgetMonitoringModule.includeHours) {
                currentAmount += parseNumber(budgetMonitoring.outstandingHoursPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedHoursPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeTravels) {
                currentAmount += parseNumber(budgetMonitoring.outstandingTravelsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedTravelsPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeDisbursements) {
                currentAmount += parseNumber(budgetMonitoring.outstandingDisbursementsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedDisbursementsPrice, 'en')
                }
            }

            console.log(`TimeForm.budgetMonitoringModule: currentPrice=${currentAmount}`)
            currentAmount += Math.max(
                0,
                multiplyTime(values.duration, parseNumber(values.rate, 'en')) -
                    multiplyTime(props.entity?.duration || '0:00', parseNumber(props.entity?.rate?.amount || '0.00', 'en')),
            )
            console.log(`TimeForm.budgetMonitoringModule: newPrice=${currentAmount}`)

            return currentAmount > parseNumber(budgetMonitoringModule.priceThreshold, 'en')
        }

        return false
    }, [hasBudgetMonitoringModule, budgetMonitoring, values.duration])

    const durationDeviatesFromTimeDivisor = useMemo<boolean>(() => {
        if (!timeDivisorSetting?.timeDivisor || timeDivisorSetting?.timeDivisor < 2) {
            return false
        }

        const time = parseTimeInMinutes(values.duration)
        return time > 0 && time % settings?.timeDivisor > 0
    }, [values.duration, settings])

    useEffect(() => {
        props.setValidationSchema(getValidationSchema(hasTimeCategoriesModule, translate))

        if (hasTimeCategoriesModule) {
            const defaultCategory = defaultTimeCategory(initialValues.emailInbound)
            const currentCategoryKey = useInitialCategory ? initialValues.category : (values.category ?? defaultCategory?.key)
            setFieldValue('category', currentCategoryKey)
        } else {
            setFieldValue('category', useInitialCategory ? initialValues.category : undefined)
        }
    }, [hasTimeCategoriesModule, timeCategories])

    useEffect(() => {
        let description = values.description
        if (useInitialDescription) {
            description = initialValues.description
        } else {
            const currentCategory = (timeCategories || []).find(cat => cat.key === values.category)
            if (currentCategory?.defaultDescription && currentCategory?.appendDescription && values.description?.length > 0) {
                description = `${values.description} / ${currentCategory?.defaultDescription}`
            } else if (currentCategory?.defaultDescription) {
                description = currentCategory?.defaultDescription
            }
        }

        setFieldValue('description', description)
    }, [hasTimeCategoriesModule, timeCategories, values.category])

    // Set fileId when we've selected an email in Outlook and email has been processed.
    useEffect(() => {
        if (isOfficeInitialized() && fileIdMap?.size === 1 && !values.fileId) {
            const [fileId] = fileIdMap.values()
            setFieldValue('fileId', fileId)
        }
    }, [fileIdMap])

    const durationSuggestions = useMemo<string[]>(() => {
        if (!timeDivisorSetting.timeDivisor || timeDivisorSetting.timeDivisor < 2) {
            return []
        }

        return [
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 2),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 3),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 4),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 5),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 6),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 7),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 8),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 9),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 10),
        ]
    }, [timeDivisorSetting])

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            {isInvoiceDraft && (
                <MessageBar intent={'warning'} layout={'multiline'}>
                    <MessageBarBody>{translate('time.edit.form.invoice.draft')}</MessageBarBody>
                </MessageBar>
            )}
            {isInvoiceFinal && (
                <MessageBar intent={'error'} layout={'multiline'}>
                    <MessageBarBody>{translate('time.edit.form.invoice.final')}</MessageBarBody>
                </MessageBar>
            )}
            {budgetMonitoringModule && budgetMonitoring && (
                <>
                    {exceedsBudget && (
                        <MessageBar intent={'error'}>
                            <MessageBarBody>{translate('time.edit.form.budget.hours.exceeds')}</MessageBarBody>
                        </MessageBar>
                    )}
                    <MessageBar intent={'info'}>
                        <MessageBarBody>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: [
                                        translate('time.edit.form.budget.info.title'),
                                        budgetMonitoringModule.includeHours && budgetMonitoringModule.includeBilled
                                            ? translate('time.edit.form.budget.info.billedhours', {
                                                  billedHours: parseTimeInHours(budgetMonitoring.billedHours),
                                                  billedHoursPrice: formatInlineCurrency(budgetMonitoring.billedHoursPrice, currency),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.includeHours
                                            ? translate('time.edit.form.budget.info.outstandinghours', {
                                                  outstandingHours: parseTimeInHours(budgetMonitoring.outstandingHours),
                                                  outstandingHoursPrice: formatInlineCurrency(budgetMonitoring.outstandingHoursPrice, currency),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.includeTravels && budgetMonitoringModule.includeBilled
                                            ? translate('time.edit.form.budget.info.billedtravels', {
                                                  ...budgetMonitoring,
                                                  billedTravelsPrice: formatInlineCurrency(budgetMonitoring.billedTravelsPrice, currency),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.includeTravels
                                            ? translate('time.edit.form.budget.info.outstandingtravels', {
                                                  ...budgetMonitoring,
                                                  outstandingTravelsPrice: formatInlineCurrency(budgetMonitoring.outstandingTravelsPrice, currency),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.includeDisbursements && budgetMonitoringModule.includeBilled
                                            ? translate('time.edit.form.budget.info.billeddisbursements', {
                                                  ...budgetMonitoring,
                                                  billedDisbursementsPrice: formatInlineCurrency(budgetMonitoring.billedDisbursementsPrice, currency),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.includeDisbursements
                                            ? translate('time.edit.form.budget.info.outstandingdisbursements', {
                                                  ...budgetMonitoring,
                                                  outstandingDisbursementsPrice: formatInlineCurrency(
                                                      budgetMonitoring.outstandingDisbursementsPrice,
                                                      currency,
                                                  ),
                                              })
                                            : undefined,
                                        budgetMonitoringModule.hourThreshold > 0
                                            ? translate('time.edit.form.budget.info.hourthreshold', {
                                                  ...budgetMonitoringModule,
                                              })
                                            : undefined,
                                        parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0
                                            ? translate('time.edit.form.budget.info.pricethreshold', {
                                                  ...budgetMonitoringModule,
                                                  priceThreshold: formatInlineCurrency(budgetMonitoringModule.priceThreshold, currency),
                                              })
                                            : undefined,
                                    ]
                                        .filter(Boolean)
                                        .join('<br/>'),
                                }}
                            ></div>
                        </MessageBarBody>
                    </MessageBar>
                </>
            )}
            {durationDeviatesFromTimeDivisor && (
                <MessageBar intent={'warning'}>
                    <MessageBarBody>{translate('time.edit.form.divisor', { divisor: settings?.timeDivisor })}</MessageBarBody>
                </MessageBar>
            )}

            {!props.useFileContext && (
                <Field label={translate('base.filepicker.label')} required validationMessage={errors?.fileId}>
                    <FileCombobox
                        required
                        readOnly={isInvoiceFinal || isInvoiceDraft}
                        disabled={isInvoiceFinal || isInvoiceDraft}
                        selectedOptions={[values.fileId]}
                        onOptionSelect={(_, data) => {
                            setFieldValue('fileId', data.selectedOptions?.[0])
                        }}
                        componentRef={fileFocusRef}
                    />
                </Field>
            )}

            {hasTimeCategoriesModule && (
                <FormikComboboxField<TimeMutationDto, EnumMapComboboxProps<string>>
                    required={hasTimeCategoriesModule}
                    labelKey={'time.category.label'}
                    fieldName={'category'}
                    component={EnumMapCombobox<string>}
                    componentProps={{
                        values: timeCategoryOptions,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        componentRef: categoryFocusRef,
                    }}
                />
            )}

            <FormikTextField
                context={context}
                label={translate('time.description')}
                fieldName="description"
                textFieldProps={{
                    required: true,
                    multiline: true,
                    rows: 3,
                    autoAdjustHeight: true,
                    readOnly: isInvoiceFinal,
                    disabled: isInvoiceFinal,
                    componentRef: descriptionFocusRef,
                    onRenderLabel: (textFieldProps: ITextFieldProps, defaultRender?: (props?: ITextFieldProps) => JSX.Element | null) => (
                        <Stack horizontal verticalAlign={'center'}>
                            {defaultRender(textFieldProps)}
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                tabIndex={-1}
                                onClick={() => {
                                    setFieldValue('description', '')
                                    descriptionFocusRef.current.focus()
                                }}
                                disabled={!values.description || isInvoiceFinal}
                                className={styles.resetDescription}
                            />
                        </Stack>
                    ),
                }}
            />
            <Stack horizontal={isViewport('>=', Viewport.Medium)} tokens={{ childrenGap: getGap() }}>
                <StackItem grow>
                    <DurationTextField
                        label={translate('time.duration.label')}
                        value={values.duration}
                        errorMessage={errors?.duration}
                        onChange={handleChange('duration')}
                        readOnly={isInvoiceFinal}
                        disabled={isInvoiceFinal}
                        componentRef={durationFocusRef}
                    />
                </StackItem>
                {modules.has(ModuleType.Invoices) && (
                    <>
                        <StackItem grow>
                            <MoneyField
                                label={translate('time.rate')}
                                value={rateField}
                                prefix={currencySymbol}
                                errorMessage={errors?.rate}
                                onChange={(_: FormEvent, newValue?: string) => setRateField(newValue || '0,00')}
                                onFormat={(_: FormEvent, newValue?: string) =>
                                    setFieldValue('rate', transformCurrency(newValue, Direction.FieldToDto))
                                }
                                readOnly={isInvoiceFinal || values.billable !== Billable.Billable}
                                disabled={isInvoiceFinal || values.billable !== Billable.Billable}
                                tabIndex={-1}
                            />
                        </StackItem>
                        <StackItem grow>
                            <MoneyField
                                label={translate('time.edit.total')}
                                value={turnoverField}
                                prefix={currencySymbol}
                                readOnly
                                disabled={isInvoiceFinal}
                                tabIndex={-1}
                            />
                        </StackItem>
                    </>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: getGap() }}>
                {durationSuggestions.length > 0 && !(isInvoiceFinal || isInvoiceDraft) && (
                    <OverflowButtonPanel
                        data={durationSuggestions}
                        minimumVisible={3}
                        overflowAxis={'horizontal'}
                        onItemClick={value => setFieldValue('duration', value)}
                    />
                )}
            </Stack>

            {props.useFileContext && (
                <Field label={translate('base.filepicker.label')} required validationMessage={errors?.fileId}>
                    <FileCombobox
                        required
                        readOnly={isInvoiceFinal}
                        disabled={isInvoiceFinal}
                        selectedOptions={[values.fileId]}
                        onOptionSelect={(_, data) => {
                            setFieldValue('fileId', data.selectedOptions?.[0])
                        }}
                        tabIndex={-1}
                    />
                </Field>
            )}

            <Field label={translate('time.userid')} required validationMessage={errors?.userId}>
                <UserCombobox
                    required
                    readOnly={isInvoiceFinal || !roles.any(rolesWriteAllTimes)}
                    disabled={isInvoiceFinal || !roles.any(rolesWriteAllTimes)}
                    selectedOptions={[values.userId]}
                    onOptionSelect={(_, data) => {
                        setFieldValue('userId', data.selectedOptions?.[0])
                    }}
                    tabIndex={-1}
                />
            </Field>
            <Field label={translate('time.date')} required validationMessage={errors?.userId}>
                <LocalDatePicker
                    isoValue={values.date}
                    onSelectDate={date => setFieldValue('date', date)}
                    readOnly={isInvoiceFinal}
                    disabled={isInvoiceFinal}
                />
            </Field>
            {modules.has(ModuleType.Invoices) && (
                <Field label={translate('time.billable')}>
                    <RadioGroup
                        value={billableField}
                        onChange={(_, data) => {
                            setBillableField(data?.value)
                            setFieldValue('billable', data?.value)
                        }}
                        disabled={isInvoiceFinal}
                        required
                    >
                        {visibleBillableOptions.map((option, index) => (
                            <Radio key={index} {...option} disabled={isInvoiceFinal} readOnly={isInvoiceFinal} />
                        ))}
                    </RadioGroup>
                </Field>
            )}
        </Stack>
    )
}

export default function TimeForm(props: Props): ReactElement {
    const { currentUser } = useUser()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)
    const [validationSchema, setValidationSchema] = useState<Schema>()
    const timeDivisorSetting = useTimeDivisorSetting()

    return (
        <Formik<TimeMutationDto>
            initialValues={
                props.entity?.data
                    ? mapTimeOverviewDtoToMutation(props.entity.data)
                    : {
                          description: '',
                          fileId: props.useFileContext ? currentFile?.id : undefined,
                          userId: currentUser?.id,
                          billable: undefined,
                          date: formatIsoDate(new Date()),
                          duration: timeDivisorSetting.initialDuration,
                          currency: undefined,
                          rate: undefined,
                          category: undefined,
                          ...props.initialValues,
                      }
            }
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={props.onSubmit}
        >
            <Form globalErrors={props.globalError ? [props.globalError] : undefined}>
                <FormContainer>
                    <TimeFormContainer
                        entity={props.entity?.data}
                        useFileContext={props.useFileContext}
                        invoiceStatus={props.entity?.data?.invoice?.status}
                        currentFile={currentFile ? currentFile : undefined}
                        setValidationSchema={setValidationSchema}
                    />
                    {props.children}
                </FormContainer>
            </Form>
        </Formik>
    )
}
